import * as React from "react";
import { Tabs } from "./tabs";
import { Domain, DomainPortRoute } from "../../schema/types/domain/entity";
import { Card, Tag } from "antd";
import { CardContent } from "../../components/antd/card/CardContent";
import { FormItem } from "../../components/antd/form/FormItem";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { FormItemValue } from "../../components/antd/form/FormItemValue";
import { domainToInput } from "./create/utils/utils";
import { InternalLink } from "../../newcomponents/table/components/InternalLink";
import { parseLink } from "../../utils/linkParser/linkParser";
import { CardTitle } from "../../components/antd/card/CardTitle";

interface Props {
  domain: Domain;
}

function formatRoutingMode(mode: string): string {
  if (mode === "pathBased") return "Path Based";
  if (mode === "gvcSubdomain") return "GVC Subdomain";
  if (mode === "workloadSubdomain") return "Workload Subdomain";
  if (mode === "none") return "None";
  return mode;
}

function getWorkloadDisplayName(workloadLink: string): { name: string; link: string } | null {
  if (!workloadLink) return null;
  const parsed = parseLink(workloadLink, { useInputCtx: true });
  return { name: parsed.name || parsed.gvcRelative || workloadLink, link: parsed.absolute || workloadLink };
}

function getRouteHeaders(route: DomainPortRoute): { name: string; value: string }[] {
  const headers: { name: string; value: string }[] = [];
  const set = route.headers?.request?.set || {};
  for (const [name, value] of Object.entries(set)) {
    headers.push({ name, value: String(value) });
  }
  return headers;
}

export const DomainSpec: React.FC<Props> = ({ domain }) => {
  const [activePortIndex, setActivePortIndex] = React.useState(0);
  const input = React.useMemo(() => domainToInput(domain), [domain]);

  const spec = domain.spec!;

  const basicWorkloadName = parseLink(input.basic.workload.link, { useInputCtx: true }).name;
  const gvcName = parseLink(input.gvc.link, { useInputCtx: true }).name;

  if (input.meta.mode === "basic") {
    return (
      <div className="cpln-form-section cpln-ant-card-container" data-section-id={"spec"}>
        <Card id={"spec"}>
          <CardTitle>Spec | Basic</CardTitle>
          <CardContent>
            <FormItem>
              <FormLabel>Workload</FormLabel>
              <FormItemValue>
                <InternalLink label={basicWorkloadName} to={`/console${input.basic.workload.link}`} />
              </FormItemValue>
            </FormItem>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container" data-section-id={"spec"}>
      <Card id={"spec"}>
        <CardTitle>Spec | Advanced</CardTitle>
        <CardContent>
          {/* General Configuration Section */}
          <div className="mb-6">
            <div className="mb-2">
              <FormLabel>General Configuration</FormLabel>
            </div>
            <div className="flex items-start gap-4 mb-2">
              <div className="flex-1">
                <FormItem>
                  <FormLabel>DNS Mode</FormLabel>
                  <FormItemValue>{spec.dnsMode?.toUpperCase() || "-"}</FormItemValue>
                </FormItem>
              </div>
              <div className="flex-1">
                <FormItem>
                  <FormLabel>Cert Challenge Type</FormLabel>
                  <FormItemValue>{spec.certChallengeType?.toUpperCase() || "Unset"}</FormItemValue>
                </FormItem>
              </div>
              <div className="flex-1">
                <FormItem>
                  <FormLabel>Routing Mode</FormLabel>
                  <FormItemValue>{formatRoutingMode(input.advanced.spec.routingMode)}</FormItemValue>
                </FormItem>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <FormItem>
                  <FormLabel>Accept All Hosts</FormLabel>
                  <FormItemValue>{spec.acceptAllHosts ? "Yes" : "No"}</FormItemValue>
                </FormItem>
              </div>
              <div className="flex-1">
                <FormItem>
                  <FormLabel>Accept All Subdomains</FormLabel>
                  <FormItemValue>{spec.acceptAllSubdomains ? "Yes" : "No"}</FormItemValue>
                </FormItem>
              </div>
              <div className="flex-1">
                {input.advanced.spec.routingMode !== "none" && gvcName ? (
                  <FormItem>
                    <FormLabel>GVC</FormLabel>
                    <FormItemValue>
                      <InternalLink label={gvcName} to={`/console${input.gvc.link}`} />
                    </FormItemValue>
                  </FormItem>
                ) : (
                  <div />
                )}
              </div>
            </div>
          </div>

          {/* Ports Section */}
          <FormLabel>Ports</FormLabel>
          <Tabs
            tabs={
              input.advanced.spec.ports.map((p, index) => ({
                label: `${p.number}/${p.protocol.toUpperCase()}`,
                isActive: index === activePortIndex,
                invalid: false,
                hide: false,
                onClick: () => setActivePortIndex(index),
              })) as any
            }
          />

          <div>
            {spec.ports.length < 1 ? <span>No Ports Found</span> : null}
            {spec.ports.map((port, index) => {
              if (index !== activePortIndex) {
                return null;
              }

              const routes = port.routes || [];
              const hasRoutes = routes.length > 0;
              const hasHostPrefix = routes.some((r) => !!r.hostPrefix);
              const hasHostRegex = routes.some((r) => !!r.hostRegex);
              const hasReplica = routes.some((r) => r.replica !== undefined);
              const hasHeaders = routes.some((r) => Object.keys(r.headers?.request?.set || {}).length > 0);
              const hasMirror = routes.some((r) => (r.mirror || []).length > 0);

              return (
                <div key={port.number}>
                  {hasRoutes ? (
                    <div className="mb-6">
                      <div className="mb-2">
                        <FormLabel>Routes</FormLabel>
                      </div>
                      <div
                        className="flex items-center table-labels px-2 py-1 border border-b-0"
                        style={{ borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
                      >
                        <div className="flex-1 flex-grow">Prefix</div>
                        <div className="flex-1 flex-grow">Regex</div>
                        <div className="flex-1 flex-grow">Replace Prefix</div>
                        {hasHostPrefix ? <div className="flex-1 flex-grow">Host Prefix</div> : null}
                        {hasHostRegex ? <div className="flex-1 flex-grow">Host Regex</div> : null}
                        <div className="w-4/12">Workload</div>
                        <div className="flex-1 flex-grow">Port</div>
                        {hasReplica ? <div className="flex-1 flex-grow">Replica</div> : null}
                      </div>
                      {routes.map((route, routeIndex) => {
                        const workloadInfo = getWorkloadDisplayName(route.workloadLink);
                        return (
                          <div
                            key={routeIndex}
                            className={`flex items-center px-2 py-1 border ${
                              routes.length - 1 !== routeIndex ? "border-b-0" : ""
                            }`}
                          >
                            <div className="flex-1 flex-grow">{route.prefix}</div>
                            <div className="flex-1 flex-grow">{route.regex}</div>
                            <div className="flex-1 flex-grow">{route.replacePrefix}</div>
                            {hasHostPrefix ? <div className="flex-1 flex-grow">{route.hostPrefix}</div> : null}
                            {hasHostRegex ? <div className="flex-1 flex-grow">{route.hostRegex}</div> : null}
                            <div className="w-4/12">
                              {workloadInfo ? (
                                <InternalLink label={workloadInfo.name} to={`/console${workloadInfo.link}`} />
                              ) : null}
                            </div>
                            <div className="flex-1 flex-grow">{route.port}</div>
                            {hasReplica ? <div className="flex-1 flex-grow">{route.replica}</div> : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  {/* Route Headers Section */}
                  {hasHeaders ? (
                    <div className="mb-6">
                      <div className="mb-2">
                        <FormLabel>Route Headers</FormLabel>
                      </div>
                      <div className="border rounded-md p-3">
                        {routes.map((route, routeIndex) => {
                          const headers = getRouteHeaders(route);
                          if (headers.length === 0) return null;
                          const routeLabel = route.prefix || route.regex || `Route ${routeIndex + 1}`;
                          return (
                            <div key={routeIndex} className="mb-2 last:mb-0">
                              <span className="font-medium">{routeLabel}:</span>{" "}
                              <span className="color-label">
                                {headers.map((h) => (
                                  <Tag key={h.name}>{h.name}: {h.value}</Tag>
                                ))}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  {/* Route Mirrors Section */}
                  {hasMirror ? (
                    <div className="mb-6">
                      <div className="mb-2">
                        <FormLabel>Route Mirrors</FormLabel>
                      </div>
                      <div className="border rounded-md p-3">
                        {routes.map((route, routeIndex) => {
                          const mirrors = route.mirror || [];
                          if (mirrors.length === 0) return null;
                          const routeLabel = route.prefix || route.regex || `Route ${routeIndex + 1}`;
                          return (
                            <div key={routeIndex} className="mb-2 last:mb-0">
                              <span className="font-medium">{routeLabel}:</span>{" "}
                              <span className="color-label">
                                {mirrors.map((m, i) => {
                                  const mirrorWorkload = getWorkloadDisplayName(m.workloadLink);
                                  const mirrorParsed = parseLink(m.workloadLink, { useInputCtx: true });
                                  const gvcLabel = mirrorParsed.gvc ? `${mirrorParsed.gvc}/` : "";
                                  const workloadLabel = gvcLabel + (mirrorWorkload?.name || m.workloadLink);
                                  const portLabel = m.port ? `:${m.port}` : "";
                                  return (
                                    <Tag key={i}>{workloadLabel}{portLabel} {m.percent}%</Tag>
                                  );
                                })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  {port.tls ? (
                    <div className={`mb-4 ${port.cors ? "border-b pb-2" : ""}`}>
                      <div className="mb-2">
                        <FormLabel>TLS</FormLabel>
                      </div>
                      <div className="flex items-center mb-2">
                        <div className="w-4/12">
                          <FormItem>
                            <FormLabel>Min Protocol Version</FormLabel>
                            <FormItemValue>{port.tls.minProtocolVersion}</FormItemValue>
                          </FormItem>
                        </div>
                        {port.tls.clientCertificate ? (
                          <div className="w-4/12">
                            <FormItem>
                              <FormLabel>Forward Client Certificate</FormLabel>
                              <FormItemValue>
                                {port.tls.clientCertificate.secretLink
                                  ? `All Matching Certificate Authority ${
                                      port.tls.clientCertificate.secretLink.split("/").at(-1)
                                    }`
                                  : `All`}
                              </FormItemValue>
                            </FormItem>
                          </div>
                        ) : null}
                        {port.tls.serverCertificate ? (
                          <div className="w-4/12">
                            <FormItem>
                              <FormLabel>Forward Server Certificate</FormLabel>
                              <FormItemValue>
                                {port.tls.serverCertificate.secretLink
                                  ? `All Matching Certificate Authority ${
                                      port.tls.serverCertificate.secretLink.split("/").at(-1)
                                    }`
                                  : `All`}
                              </FormItemValue>
                            </FormItem>
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <FormLabel>Cipher Suites</FormLabel>
                        {(port.tls.cipherSuites || []).length < 1 ? (
                          <div>No Items</div>
                        ) : (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(port.tls.cipherSuites || []).map((i) => (
                              <Tag key={i}>{i}</Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                  {port.cors ? (
                    <div className="mt-2">
                      <div className="mb-2">
                        <FormLabel>CORS</FormLabel>
                      </div>
                      <div className="flex items-center mb-2">
                        <div className="w-4/12">
                          <FormItem>
                            <FormLabel>Allow Credentials</FormLabel>
                            <FormItemValue>{port.cors.allowCredentials ? "Yes" : "No"}</FormItemValue>
                          </FormItem>
                        </div>
                        <div className="w-4/12">
                          <FormItem>
                            <FormLabel>Max Age</FormLabel>
                            <FormItemValue>{port.cors.maxAge}</FormItemValue>
                          </FormItem>
                        </div>
                      </div>
                      <div className="mb-2">
                        <FormLabel>Allow Origin | Exact Values</FormLabel>
                        {(port.cors.allowOrigins || []).filter((ao) => !!ao.exact).length < 1 ? (
                          <div>No Items</div>
                        ) : (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(port.cors.allowOrigins || [])
                              .filter((ao) => !!ao.exact)
                              .map((i) => (
                                <Tag key={i.exact}>{i.exact}</Tag>
                              ))}
                          </div>
                        )}
                      </div>
                      <div className="mb-2">
                        <FormLabel>Allow Origin | Regex Values</FormLabel>
                        {(port.cors.allowOrigins || []).filter((ao) => !!ao.regex).length < 1 ? (
                          <div>No Items</div>
                        ) : (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(port.cors.allowOrigins || [])
                              .filter((ao) => !!ao.regex)
                              .map((i) => (
                                <Tag key={i.regex}>{i.regex}</Tag>
                              ))}
                          </div>
                        )}
                      </div>
                      <div className="mb-2">
                        <FormLabel>Allow Methods</FormLabel>
                        {(port.cors.allowMethods || []).length < 1 ? (
                          <div>No Items</div>
                        ) : (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(port.cors.allowMethods || []).map((i) => (
                              <Tag key={i}>{i}</Tag>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="mb-2">
                        <FormLabel>Allow Headers</FormLabel>
                        {(port.cors.allowHeaders || []).length < 1 ? (
                          <div>No Items</div>
                        ) : (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(port.cors.allowHeaders || []).map((i) => (
                              <Tag key={i}>{i}</Tag>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="mb-2">
                        <FormLabel>Expose Headers</FormLabel>
                        {(port.cors.exposeHeaders || []).length < 1 ? (
                          <div>No Items</div>
                        ) : (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(port.cors.exposeHeaders || []).map((i) => (
                              <Tag key={i}>{i}</Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
